import { useMemo, useState } from "react";
import { Image } from "react-native";
import { getBlock, type ContentBlock } from "@src/packages/content";
import { useTranslations } from "@src/packages/i18n";
import TestDetailSheet from "../../../components/TestDetailSheet";
import ProcedureDetailSheet from "../../../components/ProcedureDetailSheet";
import { AppointmentDetailSheet } from "../modals/AppointmentDetailSheet";
import { localizeAppointmentTitle } from "../localizeAppointmentTitle";
import { Row, type RowSurface } from "@louise/moon-ui";
import type {
  AppointmentDetailItem,
  AppointmentScheduleItem,
} from "../schedule.types";

const appointmentIconImage =
  require("../../../../assets/images/stethoscope-copper.png") as number;

type Props = {
  item: AppointmentScheduleItem & { date?: string };
  /** Match the surrounding surface when the row is rendered standalone. */
  surface?: RowSurface;
  /** Optional right-side label like "8:30 AM" or "Tomorrow" */
  dateLabel?: string;
  /** Called on press when this row has a custom action */
  onPress?: () => void;
  /** Called when user taps Edit in the factsheet detail view */
  onEdit?: (appt: AppointmentScheduleItem) => void;
};

export default function AppointmentRow({
  item,
  surface = "plain",
  dateLabel,
  onPress: externalOnPress,
  onEdit,
}: Props) {
  const t = useTranslations();
  const [showAppointmentDetail, setShowAppointmentDetail] = useState(false);
  const [showEducationSheet, setShowEducationSheet] = useState(false);

  const factsheet = useMemo(() => {
    if (!item.factsheetId) return null;
    return getBlock(item.factsheetId) ?? null;
  }, [item.factsheetId]);
  const appointment = useMemo<AppointmentDetailItem | null>(
    () => (item.date ? { ...item, date: item.date } : null),
    [item],
  );
  const procedureFactsheet: ContentBlock<"procedure"> | null =
    factsheet?.kind === "procedure"
      ? (factsheet as ContentBlock<"procedure">)
      : null;
  const testFactsheet: ContentBlock<"test"> | ContentBlock<"product"> | null =
    factsheet?.kind === "test" || factsheet?.kind === "product"
      ? (factsheet as ContentBlock<"test"> | ContentBlock<"product">)
      : null;

  const rightLabel = dateLabel ?? item.time;
  const displayTitle = localizeAppointmentTitle(item.title, t);
  const handleRowPress =
    externalOnPress || appointment || factsheet
      ? () => {
          if (externalOnPress) {
            externalOnPress();
          } else if (appointment) {
            setShowAppointmentDetail(true);
          } else if (factsheet) {
            setShowEducationSheet(true);
          }
        }
      : undefined;

  const handleOpenEducation = () => {
    setShowAppointmentDetail(false);
    setShowEducationSheet(true);
  };

  return (
    <>
      <Row
        surface={surface}
        leadingMeta={rightLabel}
        leading={
          <Image
            source={appointmentIconImage}
            style={{ width: 28, height: 28 }}
            resizeMode="contain"
          />
        }
        leadingMetaWidth={62}
        leadingSize={30}
        title={displayTitle}
        subtitle={item.subtitle}
        onPress={handleRowPress}
        // Row captures for itself; the visible title names the appointment.
        action="appointment_row"
        chevron={!!handleRowPress}
        paddingHorizontal={16}
        paddingVertical={12}
      />

      <AppointmentDetailSheet
        visible={showAppointmentDetail && appointment !== null}
        appointment={appointment}
        onClose={() => setShowAppointmentDetail(false)}
        onEdit={(nextAppointment) => {
          setShowAppointmentDetail(false);
          onEdit?.(nextAppointment);
        }}
        onOpenEducation={factsheet ? handleOpenEducation : undefined}
      />

      {showEducationSheet && procedureFactsheet && (
        <ProcedureDetailSheet
          item={procedureFactsheet}
          onClose={() => setShowEducationSheet(false)}
          appointment={item}
          onEdit={onEdit}
        />
      )}

      {showEducationSheet && testFactsheet && (
        <TestDetailSheet
          item={testFactsheet}
          onClose={() => setShowEducationSheet(false)}
          returnTab="Care"
        />
      )}
    </>
  );
}
